import { useState } from "react";
import {
  AlertTriangle,
  Bath,
  Building2,
  Check,
  Droplets,
  Package,
  UserRound,
  X,
} from "lucide-react";
import ToiletWaterSchoolsModal from "@/components/ToiletWaterSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "0%");

const buildWashGaugeStyle = (value) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#14b8a6 0 ${Math.min(value, 100)}%, rgba(20,184,166,.14) ${Math.min(value, 100)}% 100%)`,
});

const buildRingStyle = (value, color = "#14b8a6") => ({
  "--tw-ring-value": `${Math.min(value, 100)}%`,
  background: `radial-gradient(circle at center, #fff 0 57%, transparent 58%), conic-gradient(${color} 0 ${Math.min(value, 100)}%, rgba(20,184,166,.14) ${Math.min(value, 100)}% 100%)`,
});

const getToiletPriority = (waterPct, zeroAlerts) => {
  if (waterPct < 87 || zeroAlerts >= 28) return { label: "Critical", tone: "danger" };
  if (waterPct < 92) return { label: "Water Sprint", tone: "warn" };
  return { label: "Monitor", tone: "" };
};

const GenderBarRow = ({ label, total, functional, withWater, maxScale }) => {
  const scale = maxScale > 0 ? maxScale : 1;
  const barWidth = (value) => `${Math.min((value / scale) * 100, 100)}%`;

  return (
    <div className="ct-gender-row">
      <div className="ct-gender-label">{label}</div>
      <div className="ct-track-stack">
        <div className="ct-track">
          <div className="ct-bar total" style={{ width: barWidth(total) }} />
        </div>
        <div className="ct-track">
          <div className="ct-bar functional" style={{ width: barWidth(functional) }} />
        </div>
        <div className="ct-track">
          <div className="ct-bar water" style={{ width: barWidth(withWater) }} />
        </div>
      </div>
      <div className="ct-gender-value">{fmt(total)}</div>
    </div>
  );
};

const TOILET_WATER_DRILL_CATEGORIES = {
  non_functional_toilets: { id: "non_functional_toilets", label: "Functional Toilets" },
  water_gap: { id: "water_gap", label: "Water Coverage" },
  zero_toilet: { id: "zero_toilet", label: "Zero-Toilet Alerts" },
};

const ClassroomsToiletsToiletWaterTab = ({
  overview,
  blockData,
  toiletDistribution,
  constructionStatus,
  scopeLabel,
}) => {
  const [activeCategory, setActiveCategory] = useState(null);

  const openCategory = (key, value) => {
    const count = Number(value) || 0;
    if (count <= 0) return;
    setActiveCategory({ ...TOILET_WATER_DRILL_CATEGORIES[key], value: fmt(value) });
  };

  if (!overview) {
    return (
      <div className="ct-empty-card">
        <Bath className="mx-auto mb-3 opacity-40" size={40} />
        <p>No toilet and water data available for the selected scope.</p>
      </div>
    );
  }

  const washIndex = overview.wash_compliance_index ?? 0;
  const functionalPct = overview.toilet_functional_pct ?? 0;
  const waterPct = overview.water_coverage_pct ?? 0;
  const waterGap = overview.water_gap ?? 0;
  const zeroTotal =
    (overview.zero_boys_toilet_schools ?? 0) + (overview.zero_girls_toilet_schools ?? 0);
  const nonFunctionalToilets = Math.max(0, (overview.total_toilets ?? 0) - (overview.total_functional ?? 0));
  const highCompliance = washIndex >= 90;

  const genderRows = toiletDistribution?.by_gender ?? [
    {
      gender: "Boys",
      total: overview.boys_toilets_total,
      functional: overview.boys_toilets_functional,
      with_water: overview.boys_toilets_water,
    },
    {
      gender: "Girls",
      total: overview.girls_toilets_total,
      functional: overview.girls_toilets_functional,
      with_water: overview.girls_toilets_water,
    },
  ];
  const maxGenderScale = Math.max(...genderRows.map((row) => row.total || 0), 1);

  const rankedBlocks = [...(blockData ?? [])]
    .sort((a, b) => (b.functional_pct ?? 0) - (a.functional_pct ?? 0))
    .map((block, index) => ({
      ...block,
      rank: index + 1,
      zero_alerts: block.zero_toilet_schools ?? 0,
    }));

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pipeline = constructionStatus ?? {
    toilets_under_construction: overview.toilets_under_construction ?? 0,
    classrooms_under_construction: overview.classrooms_under_construction ?? 0,
    classrooms_dilapidated: overview.classrooms_dilapidated ?? 0,
  };

  return (
    <div className="ct-tw-tab" data-testid="tab-toilets-content">
      <section className="ct-hero-card ct-hero-card-wash">
        <div className="ct-hero-layout">
          <div>
            <div className="ct-health-main">
              <div
                className="ct-gauge ct-gauge-wash"
                style={buildWashGaugeStyle(washIndex)}
                aria-label={`WASH Compliance Score ${pct(washIndex)}`}
              >
                <div>
                  <strong>{pct(washIndex)}</strong>
                  <span>WASH</span>
                </div>
              </div>
              <div className="ct-hero-copy">
                <h2>Water-Enabled Sanitation Command Centre</h2>
                <p>
                  Overall WASH compliance is {highCompliance ? "strong" : "improving"}, but the district still
                  has a critical water-access gap: <b>{fmt(waterGap)} functional toilets</b> require water
                  provisioning and <b>{fmt(zeroTotal)} schools</b> need gender-specific toilet closure.
                </p>
                <div className="ct-status-row">
                  <HeroStatusChip
                    chipClass="ct-status-chip ct-status-chip-teal"
                    onClick={!highCompliance ? () => document.getElementById("ct-tw-actions")?.scrollIntoView({ behavior: "smooth" }) : undefined}
                    ariaLabel={!highCompliance ? "Needs focus — view WASH action queue" : undefined}
                  >
                    {highCompliance ? "High compliance" : "Needs focus"}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant="orange"
                    onClick={waterGap > 0 ? () => openCategory("water_gap", waterGap) : undefined}
                    ariaLabel={waterGap > 0 ? `Water gap ${fmt(waterGap)} — view schools needing water` : undefined}
                  >
                    Water gap: {fmt(waterGap)}
                  </HeroStatusChip>
                  <HeroStatusChip chipClass="ct-status-chip" variant="blue" showChevron={false}>
                    {fmt(overview.total_blocks)} blocks monitored
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="ct-summary-note">
              <div className="ct-mini-icon cyan">
                <Droplets size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>
                  Run a 30-day water connection sprint before new construction review. Prioritise functional
                  toilets without water before adding new units.
                </span>
              </div>
            </div>
          </div>
          <div className="ct-metric-grid" aria-label="Toilet and water KPIs">
            <article className="ct-metric-card">
              <small>Total Toilets</small>
              <strong>{fmt(overview.total_toilets)}</strong>
              <span className="ct-metric-sub">District toilet asset base</span>
            </article>
            <article
              className={`ct-metric-card${nonFunctionalToilets > 0 ? " clickable" : ""}`}
              role={nonFunctionalToilets > 0 ? "button" : undefined}
              tabIndex={nonFunctionalToilets > 0 ? 0 : undefined}
              onClick={() => openCategory("non_functional_toilets", nonFunctionalToilets)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openCategory("non_functional_toilets", nonFunctionalToilets);
                }
              }}
              title={nonFunctionalToilets > 0 ? "View schools with non-functional toilets" : undefined}
            >
              <small>Functional Toilets</small>
              <strong>{fmt(overview.total_functional)}</strong>
              <span className="ct-metric-sub">{pct(functionalPct)} functional readiness</span>
            </article>
            <article
              className={`ct-metric-card warning${waterGap > 0 ? " clickable" : ""}`}
              role={waterGap > 0 ? "button" : undefined}
              tabIndex={waterGap > 0 ? 0 : undefined}
              onClick={() => openCategory("water_gap", waterGap)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openCategory("water_gap", waterGap);
                }
              }}
              title={waterGap > 0 ? "View schools missing water coverage on functional toilets" : undefined}
            >
              <small>Water Coverage</small>
              <strong>{pct(waterPct)}</strong>
              <span className="ct-metric-sub">{fmt(waterGap)} toilets need water</span>
            </article>
            <article className="ct-metric-card">
              <small>Boys Toilets</small>
              <strong>{fmt(overview.boys_toilets_total)}</strong>
              <span className="ct-metric-sub">{pct(overview.boys_functional_pct)} functional</span>
            </article>
            <article className="ct-metric-card">
              <small>Girls Toilets</small>
              <strong>{fmt(overview.girls_toilets_total)}</strong>
              <span className="ct-metric-sub">{pct(overview.girls_functional_pct)} functional</span>
            </article>
            <article
              className={`ct-metric-card danger${zeroTotal > 0 ? " clickable" : ""}`}
              role={zeroTotal > 0 ? "button" : undefined}
              tabIndex={zeroTotal > 0 ? 0 : undefined}
              onClick={() => openCategory("zero_toilet", zeroTotal)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openCategory("zero_toilet", zeroTotal);
                }
              }}
              title={zeroTotal > 0 ? "View schools with zero-toilet alerts" : undefined}
            >
              <small>Zero-Toilet Alerts</small>
              <strong>{fmt(zeroTotal)}</strong>
              <span className="ct-metric-sub">
                {fmt(overview.zero_boys_toilet_schools)} boys + {fmt(overview.zero_girls_toilet_schools)} girls
                gaps
              </span>
            </article>
          </div>
        </div>
      </section>

      <section className="ct-kpi-strip" aria-label="Toilet and water KPI summary">
        <article className="ct-kpi-card">
          <div className="ct-kpi-top">
            <div>
              <small>Total Toilets</small>
              <strong>{fmt(overview.total_toilets)}</strong>
              <span>Across {fmt(overview.total_schools)} schools</span>
            </div>
            <div className="ct-mini-icon">
              <Bath size={20} />
            </div>
          </div>
        </article>
        <article className="ct-kpi-card green">
          <div className="ct-kpi-top">
            <div>
              <small>Functional</small>
              <strong>{fmt(overview.total_functional)}</strong>
              <span>{pct(functionalPct)} ready to use</span>
            </div>
            <div className="ct-mini-icon green">
              <Check size={20} />
            </div>
          </div>
        </article>
        <article className="ct-kpi-card cyan">
          <div className="ct-kpi-top">
            <div>
              <small>With Water</small>
              <strong>{pct(waterPct)}</strong>
              <span>Coverage across functional toilets</span>
            </div>
            <div className="ct-mini-icon cyan">
              <Droplets size={20} />
            </div>
          </div>
        </article>
        <article className="ct-kpi-card orange">
          <div className="ct-kpi-top">
            <div>
              <small>Water Gap</small>
              <strong>{fmt(waterGap)}</strong>
              <span>Functional but no water</span>
            </div>
            <div className="ct-mini-icon orange">
              <AlertTriangle size={20} />
            </div>
          </div>
        </article>
        <article className="ct-kpi-card purple">
          <div className="ct-kpi-top">
            <div>
              <small>Girls Toilets</small>
              <strong>{fmt(overview.girls_toilets_total)}</strong>
              <span>{pct(overview.girls_functional_pct)} functional</span>
            </div>
            <div className="ct-mini-icon purple">
              <UserRound size={20} />
            </div>
          </div>
        </article>
        <article className="ct-kpi-card red">
          <div className="ct-kpi-top">
            <div>
              <small>No Toilet Schools</small>
              <strong>{fmt(zeroTotal)}</strong>
              <span>Gender-specific access gaps</span>
            </div>
            <div className="ct-mini-icon red">
              <X size={20} />
            </div>
          </div>
        </article>
      </section>

      <div className="ct-section-title">
        <div>
          <h2>Compliance and Gender-Wise Access</h2>
          <p>
            The first diagnostic layer separates sanitation readiness from water availability so officers can
            identify whether the gap is infrastructure, maintenance, or connection related.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-tw-actions")}>
          View action queue →
        </button>
      </div>

      <section className="ct-tw-dashboard-grid">
        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Toilet Compliance Metrics</h3>
              <p>Three core indicators used for district WASH readiness.</p>
            </div>
            <span className="ct-pill ct-pill-green">Healthy</span>
          </div>
          <div className="ct-score-row">
            <div className="ct-score-tile">
              <div className="ct-ring ct-ring-teal" style={buildRingStyle(functionalPct)}>
                <strong>{pct(functionalPct)}</strong>
              </div>
              <b>Functional Rate</b>
              <span>Asset usability</span>
            </div>
            <div className="ct-score-tile">
              <div className="ct-ring ct-ring-blue" style={buildRingStyle(waterPct, "#2563eb")}>
                <strong style={{ color: "#2563eb" }}>{pct(waterPct)}</strong>
              </div>
              <b>Water Coverage</b>
              <span>Water-enabled toilets</span>
            </div>
            <div className="ct-score-tile">
              <div className="ct-ring ct-ring-purple" style={buildRingStyle(washIndex, "#8b5cf6")}>
                <strong style={{ color: "#8b5cf6" }}>{pct(washIndex)}</strong>
              </div>
              <b>WASH Compliance</b>
              <span>Composite readiness</span>
            </div>
          </div>
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Toilet Status Distribution</h3>
              <p>Boys and girls toilets by total, functional, and with-water status.</p>
            </div>
            <span className="ct-pill">Gender view</span>
          </div>
          <div className="ct-gender-chart">
            {genderRows.map((row) => (
              <GenderBarRow
                key={row.gender}
                label={row.gender}
                total={row.total ?? 0}
                functional={row.functional ?? 0}
                withWater={row.with_water ?? 0}
                maxScale={maxGenderScale}
              />
            ))}
          </div>
          <div className="ct-gender-legend">
            <span className="functional">
              <i /> Functional
            </span>
            <span className="total">
              <i /> Total
            </span>
            <span className="water">
              <i /> With water
            </span>
          </div>
        </article>
      </section>

      <section className="ct-issue-grid" id="ct-tw-actions">
        <article className="ct-panel ct-alert-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Zero-Toilet School Exceptions</h3>
              <p>Immediate red-flag items for gender-inclusive school access.</p>
            </div>
            <span className="ct-pill ct-pill-red">Critical</span>
          </div>
          <div className="ct-alert-metrics">
            <div className="ct-alert-card">
              <strong>{fmt(overview.zero_boys_toilet_schools)}</strong>
              <b>No Boys Toilets</b>
              <span>{overview.zero_boys_pct}% of schools need boys toilets</span>
            </div>
            <div className="ct-alert-card">
              <strong>{fmt(overview.zero_girls_toilet_schools)}</strong>
              <b>No Girls Toilets</b>
              <span>{overview.zero_girls_pct}% of schools need girls toilets</span>
            </div>
          </div>
        </article>

        <article className="ct-panel ct-pipeline-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Construction Pipeline</h3>
              <p>Track committed WASH and infrastructure workstreams in one view.</p>
            </div>
            <span className="ct-pill">Pipeline</span>
          </div>
          <div className="ct-pipeline-list">
            <div className="ct-pipeline-item">
              <div className="ct-mini-icon cyan">
                <Bath size={20} />
              </div>
              <div>
                <b>Toilets under construction</b>
                <span>New sanitation capacity in progress</span>
              </div>
              <strong className="ct-number-badge">{fmt(pipeline.toilets_under_construction)}</strong>
            </div>
            <div className="ct-pipeline-item">
              <div className="ct-mini-icon orange">
                <Building2 size={20} />
              </div>
              <div>
                <b>Classrooms under construction</b>
                <span>Linked infrastructure construction</span>
              </div>
              <strong className="ct-number-badge orange">{fmt(pipeline.classrooms_under_construction)}</strong>
            </div>
            <div className="ct-pipeline-item">
              <div className="ct-mini-icon red">
                <AlertTriangle size={20} />
              </div>
              <div>
                <b>Dilapidated classrooms</b>
                <span>Safety-linked repair queue</span>
              </div>
              <strong className="ct-number-badge red">{fmt(pipeline.classrooms_dilapidated)}</strong>
            </div>
          </div>
        </article>
      </section>

      <div className="ct-section-title">
        <div>
          <h2>Recommended Actions</h2>
          <p>
            Action cards convert the WASH metrics into a simple operating plan for district officers and block
            education teams.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-tw-ranking")}>
          Go to block ranking →
        </button>
      </div>

      <section className="ct-tw-action-grid">
        <article className="ct-tw-action-card">
          <div className="ct-mini-icon cyan">
            <Droplets size={22} />
          </div>
          <h3>Close Water Gaps First</h3>
          <p>
            Map the {fmt(waterGap)} functional toilets without water and route them to water connection,
            storage, or repair teams before approving new construction.
          </p>
          <div className="ct-action-meta">
            <span className="ct-chip orange">30-day sprint</span>
            <span className="ct-chip">Water priority</span>
          </div>
        </article>
        <article className="ct-tw-action-card">
          <div className="ct-mini-icon red">
            <X size={22} />
          </div>
          <h3>Resolve Zero-Toilet Schools</h3>
          <p>
            Create a block-wise exception list for {fmt(overview.zero_boys_toilet_schools)} schools without boys
            toilets and {fmt(overview.zero_girls_toilet_schools)} schools without girls toilets, with weekly
            closure tracking.
          </p>
          <div className="ct-action-meta">
            <span className="ct-chip red">Critical access</span>
            <span className="ct-chip">Weekly review</span>
          </div>
        </article>
        <article className="ct-tw-action-card">
          <div className="ct-mini-icon green">
            <Check size={22} />
          </div>
          <h3>Maintain High Functionality</h3>
          <p>
            Keep the {pct(functionalPct)} functional rate stable using preventive maintenance alerts, cleaning
            audits, and minor repair escalation.
          </p>
          <div className="ct-action-meta">
            <span className="ct-chip green">Preventive</span>
            <span className="ct-chip">Officer follow-up</span>
          </div>
        </article>
      </section>

      <section className="ct-panel ct-table-card" id="ct-tw-ranking">
        <div className="ct-table-head">
          <div>
            <h3>Block-Wise Toilet and Water Readiness</h3>
            <p>Prioritised view for block officers showing access, functionality, water coverage, and WASH risk.</p>
          </div>
          <span className="ct-pill">
            {fmt(overview.total_blocks)} blocks · {fmt(overview.total_schools ?? 0)} schools
          </span>
        </div>
        <table className="ct-infra-table" aria-label="Block-wise toilet and water readiness">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Block</th>
              <th>Schools</th>
              <th>Total Toilets</th>
              <th>Functional Rate</th>
              <th>Water Coverage</th>
              <th>Zero-Toilet Alerts</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            {rankedBlocks.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", color: "var(--ct-muted)", fontWeight: 600 }}>
                  No block ranking data available for the selected scope.
                </td>
              </tr>
            ) : (
              rankedBlocks.map((block) => {
                const priority = getToiletPriority(block.water_pct ?? 0, block.zero_alerts ?? 0);
                return (
                  <tr key={block.block_name}>
                    <td>{block.rank}</td>
                    <td>{block.block_name}</td>
                    <td>{fmt(block.total_schools)}</td>
                    <td>{fmt(block.total_toilets)}</td>
                    <td className="ct-progress-cell">
                      <b>{pct(block.functional_pct)}</b>
                      <div className="ct-progress-mini">
                        <i style={{ width: `${Math.min(block.functional_pct ?? 0, 100)}%` }} />
                      </div>
                    </td>
                    <td className="ct-progress-cell">
                      <b>{pct(block.water_pct)}</b>
                      <div className="ct-progress-mini blue">
                        <i style={{ width: `${Math.min(block.water_pct ?? 0, 100)}%` }} />
                      </div>
                    </td>
                    <td>{fmt(block.zero_alerts)}</td>
                    <td>
                      <span className={`ct-tag ${priority.tone}`}>{priority.label}</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>

      <ToiletWaterSchoolsModal
        open={Boolean(activeCategory)}
        category={activeCategory}
        onClose={() => setActiveCategory(null)}
        scopeLabel={scopeLabel}
      />
    </div>
  );
};

export default ClassroomsToiletsToiletWaterTab;
